import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
  Box, TextField, Typography, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import { useGoogleMaps } from '../providers/GoogleMapsProvider';
import { updateQuota } from '../utils/googleMapsQuota';

/**
 * Lightweight Google Maps address autocomplete — no map.
 *
 * Props:
 *   onPlaceSelected({ suburb, state, location })
 *   initialLocation  — pre-fill with an existing address string
 */
export default function OutreachPlaceAutocomplete({ onPlaceSelected, initialLocation = '' }) {
  const { isLoaded, loadError } = useGoogleMaps();
  const containerRef  = useRef(null);
  const autocompleteRef = useRef(null);
  const isMounted = useRef(true);

  // The address string that was selected from the dropdown.
  // Non-empty → show the read-only "selected" TextField.
  // Empty      → show the PlaceAutocompleteElement search input.
  const [selectedAddress, setSelectedAddress] = useState(initialLocation);

  // Sync if the parent resets the location (e.g. edit mode loads existing data).
  useEffect(() => {
    setSelectedAddress(initialLocation || '');
  }, [initialLocation]);

  // ---- place-select handler ----------------------------------------
  const handlePlaceSelect = useCallback(async (event) => {
    // Track Places API usage
    try {
      updateQuota('places');
    } catch (error) {
      console.error('OutreachPlaceAutocomplete: error updating places quota:', error);
    }

    try {
      const place = event.placePrediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });

      let suburb = '', state = '';
      for (const comp of (place.addressComponents || [])) {
        const types    = comp.types || [];
        const longName  = comp.longText  || comp.long_name  || '';
        const shortName = comp.shortText || comp.short_name || '';
        if (types.includes('locality'))                    suburb = longName;
        if (types.includes('administrative_area_level_1')) state  = shortName;
      }
      const location = place.formattedAddress || '';
      setSelectedAddress(location);
      onPlaceSelected({ suburb, state, location });
    } catch (err) {
      console.error('OutreachPlaceAutocomplete: select error', err);
    }
  }, [onPlaceSelected]);

  // ---- lifecycle ---------------------------------------------------
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Set up PlaceAutocompleteElement — container stays in the DOM always
  // (we just show/hide it with CSS so the element doesn't need re-creating).
  useEffect(() => {
    if (!isLoaded || !containerRef.current) return;
    if (autocompleteRef.current) return;  // already set up

    let timeout;
    const setup = async () => {
      if (!isMounted.current || !containerRef.current) return;
      try {
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
        const el = new PlaceAutocompleteElement({
          includedRegionCodes: ['AU'],
          includedPrimaryTypes: ['geocode'],
        });

        // Style to blend with the outer MUI-like border (no own border).
        Object.assign(el.style, {
          width: '100%',
          border: 'none',
          outline: 'none',
          fontSize: '1rem',
          lineHeight: '1.4375em',
          padding: '16.5px 14px',
          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          background: 'transparent',
          boxSizing: 'border-box',
          display: 'block',
          color: 'inherit',
          colorScheme: 'light',
        });
        // CSS custom-property tokens used by the Places UI Kit's shadow DOM
        el.style.setProperty('--gmp-color-surface',             '#ffffff');
        el.style.setProperty('--gmp-color-on-surface',          '#111827');
        el.style.setProperty('--gmp-color-surface-variant',     '#f3f4f6');
        el.style.setProperty('--gmp-color-on-surface-variant',  '#4b5563');
        el.style.setProperty('--gmp-color-primary',             '#2563eb');
        el.style.setProperty('--gmp-color-outline',             'rgba(0,0,0,0.23)');

        el.addEventListener('gmp-select', handlePlaceSelect);
        autocompleteRef.current = el;
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(el);

        // Force light-theme colours on the element's shadow DOM.
        // PlaceAutocompleteElement renders its dropdown inside a shadow root;
        // regular CSS can't reach it, so we inject a <style> tag in.
        const injectTheme = () => {
          const sr = el.shadowRoot;
          if (!sr) return;
          const existing = sr.querySelector('style[data-austnews]');
          if (existing) return;
          const s = document.createElement('style');
          s.setAttribute('data-austnews', '1');
          s.textContent = `
            :host {
              --gmp-color-surface:            #ffffff;
              --gmp-color-on-surface:         #111827;
              --gmp-color-surface-variant:    #f3f4f6;
              --gmp-color-on-surface-variant: #4b5563;
              --gmp-color-primary:            #2563eb;
              --gmp-color-outline:            rgba(0,0,0,0.23);
              color-scheme: light;
            }
            * {
              color: #111827 !important;
            }
            input {
              background: transparent !important;
            }
          `;
          sr.appendChild(s);
        };

        // The shadow root might not be populated immediately — retry briefly.
        injectTheme();
        const timer = setInterval(() => {
          if (el.shadowRoot && el.shadowRoot.childElementCount > 0) {
            injectTheme();
            clearInterval(timer);
          }
        }, 100);
        setTimeout(() => clearInterval(timer), 3000);
      } catch (err) {
        console.error('OutreachPlaceAutocomplete: setup error', err);
      }
    };

    timeout = setTimeout(setup, 100);
    return () => {
      clearTimeout(timeout);
      if (autocompleteRef.current) {
        try {
          autocompleteRef.current.removeEventListener('gmp-select', handlePlaceSelect);
          if (autocompleteRef.current.parentNode) {
            autocompleteRef.current.parentNode.removeChild(autocompleteRef.current);
          }
        } catch (_) {}
        autocompleteRef.current = null;
      }
    };
  }, [isLoaded, handlePlaceSelect]);

  // ---- clear selection ---------------------------------------------
  function clearSelection() {
    setSelectedAddress('');
    onPlaceSelected({ suburb: '', state: '', location: '' });
  }

  // ---- error / loading states --------------------------------------
  if (loadError) {
    return (
      <Typography variant="caption" color="error">
        Google Maps unavailable — enter suburb and state manually below.
      </Typography>
    );
  }
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Loading Maps…</Typography>
      </Box>
    );
  }

  // ---- render ------------------------------------------------------
  return (
    <Box>
      {/* ---- Search input (PlaceAutocompleteElement) ---- */}
      {/* Always in the DOM so the element doesn't need re-creating. */}
      <Box sx={{ display: selectedAddress ? 'none' : 'block' }}>
        {/* Outer wrapper that mimics a MUI outlined TextField */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '4px',
            border: '1px solid rgba(0,0,0,0.23)',
            transition: 'border-color 200ms',
            '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
            '&:focus-within': { borderColor: 'primary.main', borderWidth: '2px' },
            '& gmp-place-autocomplete': { width: '100%', display: 'block' },
          }}
        >
          {/* Floating label — sits on top of the border like a MUI InputLabel */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: -9,
              left: 10,
              px: 0.5,
              bgcolor: 'background.paper',
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.2,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            Location
          </Typography>

          {/* PlaceAutocompleteElement container */}
          <Box ref={containerRef} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Search an address to auto-fill suburb &amp; state
        </Typography>
      </Box>

      {/* ---- Selected address (read-only MUI TextField) ---- */}
      {selectedAddress && (
        <TextField
          label="Location"
          value={selectedAddress}
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnIcon sx={{ fontSize: 18, color: 'success.main' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSelection} title="Clear and search again">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.selected' } }}
        />
      )}
    </Box>
  );
}
